import { definePreset } from '@primeuix/themes';
import Lara from '@primeuix/themes/lara';

/**
 * Design "Marine": Petrol als Primaerfarbe, blaugraue Oberflaechen, dunkelblaue Sidebar
 * (Sidebar-Farben siehe assets/layout/menutheme/_marine.scss).
 * Primaerfarbe ist bewusst {primary.700}, damit weisse Button-Schrift WCAG AA erfuellt.
 */
export const CnfPreset = definePreset(Lara, {
    semantic: {
        primary: {
            50: '#f0fdfa',
            100: '#ccfbf1',
            200: '#99f6e4',
            300: '#5eead4',
            400: '#2dd4bf',
            500: '#14b8a6',
            600: '#0d9488',
            700: '#0f766e',
            800: '#115e59',
            900: '#134e4a',
            950: '#042f2e'
        },
        colorScheme: {
            light: {
                surface: {
                    0: '#ffffff',
                    50: '#f7f9fb',
                    100: '#edf1f5',
                    200: '#e3e9ef',
                    300: '#c3ced9',
                    400: '#8fa6bd',
                    500: '#56687a',
                    600: '#34495e',
                    700: '#243a50',
                    800: '#172d44',
                    900: '#0f1e2e',
                    950: '#0b1622'
                },
                primary: {
                    color: '{primary.700}',
                    contrastColor: '#ffffff',
                    hoverColor: '{primary.800}',
                    activeColor: '{primary.900}'
                },
                highlight: {
                    background: '{primary.50}',
                    focusBackground: '{primary.100}',
                    color: '{primary.800}',
                    focusColor: '{primary.900}'
                }
            },
            dark: {
                primary: {
                    color: '{primary.400}',
                    contrastColor: '{surface.900}',
                    hoverColor: '{primary.300}',
                    activeColor: '{primary.200}'
                },
                highlight: {
                    background: 'color-mix(in srgb, {primary.400}, transparent 84%)',
                    focusBackground: 'color-mix(in srgb, {primary.400}, transparent 76%)',
                    color: 'rgba(255,255,255,.87)',
                    focusColor: 'rgba(255,255,255,.87)'
                }
            }
        }
    },
    components: {
        // Buttons kontrastreich: Gruen/Rot dunkler (weisse Schrift WCAG AA), sekundaere und
        // farbige outlined-Buttons getoent statt weiss (Flaechen siehe assets/layout/_overrides.scss).
        button: {
            colorScheme: {
                light: {
                    root: {
                        secondary: {
                            background: '#e3ebf3',
                            hoverBackground: '#d3dee9',
                            activeBackground: '#c3ced9',
                            borderColor: '#c3ced9',
                            hoverBorderColor: '#c3ced9',
                            activeBorderColor: '#8fa6bd',
                            color: '#0b2540',
                            hoverColor: '#0b2540',
                            activeColor: '#0b2540',
                            focusRing: { color: 'transparent', shadow: '0 0 0 0.2rem {surface.300}' }
                        },
                        info: {
                            background: '#1d5f99',
                            hoverBackground: '#184f80',
                            activeBackground: '#133f66',
                            borderColor: '#1d5f99',
                            hoverBorderColor: '#184f80',
                            activeBorderColor: '#133f66',
                            color: '#ffffff',
                            hoverColor: '#ffffff',
                            activeColor: '#ffffff',
                            focusRing: { color: 'transparent', shadow: '0 0 0 0.2rem #b3cfe6' }
                        },
                        success: {
                            background: '{green.700}',
                            hoverBackground: '{green.800}',
                            activeBackground: '{green.900}',
                            borderColor: '{green.700}',
                            hoverBorderColor: '{green.800}',
                            activeBorderColor: '{green.900}',
                            color: '#ffffff',
                            hoverColor: '#ffffff',
                            activeColor: '#ffffff',
                            focusRing: { color: 'transparent', shadow: '0 0 0 0.2rem {green.200}' }
                        },
                        warn: {
                            background: '{amber.700}',
                            hoverBackground: '{amber.800}',
                            activeBackground: '{amber.900}',
                            borderColor: '{amber.700}',
                            hoverBorderColor: '{amber.800}',
                            activeBorderColor: '{amber.900}',
                            color: '#ffffff',
                            hoverColor: '#ffffff',
                            activeColor: '#ffffff',
                            focusRing: { color: 'transparent', shadow: '0 0 0 0.2rem {amber.200}' }
                        },
                        danger: {
                            background: '{red.700}',
                            hoverBackground: '{red.800}',
                            activeBackground: '{red.900}',
                            borderColor: '{red.700}',
                            hoverBorderColor: '{red.800}',
                            activeBorderColor: '{red.900}',
                            color: '#ffffff',
                            hoverColor: '#ffffff',
                            activeColor: '#ffffff',
                            focusRing: { color: 'transparent', shadow: '0 0 0 0.2rem {red.200}' }
                        },
                        contrast: {
                            background: '#0b2540',
                            hoverBackground: '{surface.800}',
                            activeBackground: '{surface.700}',
                            borderColor: '#0b2540',
                            hoverBorderColor: '{surface.800}',
                            activeBorderColor: '{surface.700}',
                            color: '#ffffff',
                            hoverColor: '#ffffff',
                            activeColor: '#ffffff',
                            focusRing: { color: 'transparent', shadow: '0 0 0 0.2rem {surface.400}' }
                        }
                    },
                    outlined: {
                        primary: { hoverBackground: '{primary.100}', activeBackground: '{primary.200}', borderColor: '{primary.200}', color: '{primary.800}' },
                        secondary: { hoverBackground: '#d3dee9', activeBackground: '#c3ced9', borderColor: '#c3ced9', color: '#0b2540' },
                        success: { hoverBackground: '#c9ecd6', activeBackground: '#a8dcbe', borderColor: '#a8dcbe', color: '#13693f' },
                        info: { hoverBackground: '#cfe1f1', activeBackground: '#b3cfe6', borderColor: '#b3cfe6', color: '#1c5d8f' },
                        warn: { hoverBackground: '#fbe3b0', activeBackground: '#f0c97a', borderColor: '#f0c97a', color: '#8a4b06' },
                        danger: { hoverBackground: '#fbd5d5', activeBackground: '#f5b5b5', borderColor: '#f5b5b5', color: '#a61b1b' },
                        contrast: { hoverBackground: '#d3dee9', activeBackground: '#c3ced9', borderColor: '#0b2540', color: '#0b2540' }
                    },
                    text: {
                        secondary: { hoverBackground: '#e3ebf3', activeBackground: '#d3dee9', color: '#34495e' },
                        success: { hoverBackground: '#def5e7', activeBackground: '#c9ecd6', color: '{green.700}' },
                        info: { hoverBackground: '#e2eef8', activeBackground: '#cfe1f1', color: '#1d5f99' },
                        warn: { hoverBackground: '#fdf0d5', activeBackground: '#fbe3b0', color: '{amber.700}' },
                        danger: { hoverBackground: '#fde8e8', activeBackground: '#fbd5d5', color: '{red.700}' }
                    }
                }
            }
        },
        // Status-Tags: Grundfarbe bleibt, getoente Flaeche mit dunkler Schrift (Rahmen siehe _overrides.scss)
        tag: {
            colorScheme: {
                light: {
                    primary: { background: '{primary.100}', color: '{primary.800}' },
                    secondary: { background: '#edf1f5', color: '#34495e' },
                    success: { background: '#def5e7', color: '#13693f' },
                    info: { background: '#e2eef8', color: '#1c5d8f' },
                    warn: { background: '#fdf0d5', color: '#8a4b06' },
                    danger: { background: '#fde8e8', color: '#a61b1b' },
                    contrast: { background: '#0b2540', color: '#ffffff' }
                }
            }
        },
        // Umschalter (p-selectbutton): blaugraue Flaeche, aktiver Eintrag in Petrol
        togglebutton: {
            root: {
                checkedBackground: '{primary.color}',
                checkedColor: '{primary.contrast.color}',
                checkedBorderColor: '{primary.color}'
            },
            icon: {
                checkedColor: '{primary.contrast.color}'
            },
            colorScheme: {
                light: {
                    root: {
                        background: '#e3ebf3',
                        hoverBackground: '#d3dee9',
                        borderColor: '#c3ced9',
                        color: '#0b2540',
                        hoverColor: '#0b2540'
                    }
                }
            }
        },
        datatable: {
            colorScheme: {
                light: {
                    headerCell: {
                        background: '#e6edf4',
                        hoverBackground: '#dbe4ee',
                        color: '#0b2540'
                    }
                }
            }
        }
    }
});
